/**
 * PII Proxy Page
 *
 * Manage PII detection, masking, project registration, custom registries,
 * and consent configuration for the PII Proxy service.
 */
// React 18 with jsx runtime doesn't need React import
import { useState, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShieldCheckIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  DocumentArrowUpIcon,
  EyeIcon,
  EyeSlashIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  FingerPrintIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { rawApi } from '@/services/api'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'

// ---------- Types ----------

interface PIIProxyStatus {
  enabled: boolean
  pattern_version: string
  registered_projects: number
  active_projects: number
  shadow_projects: number
  ner_enabled: boolean
  uptime_seconds?: number
}

interface PIIProject {
  id: string
  name: string
  tenant_id: string
  enabled: boolean
  shadow_mode: boolean
  created_at: string
  updated_at: string
  patterns_override?: string[]
  description?: string
}

interface PIIMatch {
  category: string
  value: string              // masked value from backend, e.g. "123***"
  start: number
  end: number
  confidence: number
  tier: number               // 1=regex, 2=validator, 3=NER
  pattern_name: string
}

interface PIIScanResult {
  matches: PIIMatch[]
  scan_duration_ms: number
  categories_found: string[]
  pattern_version: string
}

interface RegistryTerm {
  id: string
  term: string
  category: string
  tenant_id: string
  created_at: string
}

interface RegistryStats {
  tenant_id: string
  total_terms: number
  categories: Record<string, number>
  last_updated: string
}

interface ConsentRecord {
  id: string
  tenant_id: string
  user_id: string
  granted: boolean
  scope: string
  created_at: string
  updated_at: string
}

interface NERStatus {
  available: boolean
  model_version: string
  load_time_ms: number
  gpu_enabled: boolean
  // fallback/error fields (set locally on 503)
  error?: string
}

// ---------- Constants ----------

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: ShieldCheckIcon },
  { key: 'projects', label: 'Projects', icon: ClipboardDocumentListIcon },
  { key: 'scanner', label: 'Scanner Test', icon: MagnifyingGlassIcon },
  { key: 'registry', label: 'Custom Registry', icon: FingerPrintIcon },
  { key: 'consent', label: 'Consent', icon: UserGroupIcon },
] as const

type TabKey = (typeof TABS)[number]['key']

const CHART_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6']

const CATEGORY_COLORS: Record<string, string> = {
  EMAIL: '#6366f1',
  PHONE: '#22c55e',
  SSN: '#ef4444',
  CREDIT_CARD: '#f59e0b',
  ADDRESS: '#8b5cf6',
  NAME: '#06b6d4',
  IP_ADDRESS: '#ec4899',
  DATE_OF_BIRTH: '#14b8a6',
}

// ---------- API Helpers ----------

const piiApi = {
  getStatus: () => rawApi.get<PIIProxyStatus>('/api/v1/pii-proxy/status').then((r) => r.data),
  getProjects: () => rawApi.get<PIIProject[]>('/api/v1/pii-proxy/projects').then((r) => r.data),
  registerProject: (data: { name: string; tenant_id: string; enabled?: boolean; shadow_mode?: boolean; description?: string }) =>
    rawApi.post<PIIProject>('/api/v1/pii-proxy/projects', data).then((r) => r.data),
  updateProject: (id: string, data: Partial<PIIProject>) =>
    rawApi.put<PIIProject>(`/api/v1/pii-proxy/projects/${id}`, data).then((r) => r.data),
  deleteProject: (id: string) => rawApi.delete(`/api/v1/pii-proxy/projects/${id}`),
  testScan: (text: string) => rawApi.post<PIIScanResult>('/api/v1/pii-proxy/test', { text }).then((r) => r.data),
  getRegistryStats: (tenantId: string) =>
    rawApi.get<RegistryStats>(`/api/v1/pii-proxy/registry/${tenantId}/stats`).then((r) => r.data),
  getRegistryTerms: (tenantId: string) =>
    rawApi.get<RegistryTerm[]>(`/api/v1/pii-proxy/registry/${tenantId}/terms`).then((r) => r.data),
  addRegistryTerm: (tenantId: string, term: string, category: string) =>
    rawApi.post<RegistryTerm>(`/api/v1/pii-proxy/registry/${tenantId}/terms`, { term, category }).then((r) => r.data),
  removeRegistryTerm: (tenantId: string, termId: string) =>
    rawApi.delete(`/api/v1/pii-proxy/registry/${tenantId}/terms/${termId}`),
  importCSV: (tenantId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return rawApi.post(`/api/v1/pii-proxy/registry/${tenantId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  getConsentRecords: (tenantId: string) =>
    rawApi.get<ConsentRecord[]>(`/api/v1/pii-proxy/consent/${tenantId}`).then((r) => r.data),
  grantConsent: (tenantId: string, userId: string, scope: string) =>
    rawApi.post<ConsentRecord>(`/api/v1/pii-proxy/consent/${tenantId}/grant`, { user_id: userId, scope }).then((r) => r.data),
  revokeConsent: (tenantId: string, consentId: string) =>
    rawApi.delete(`/api/v1/pii-proxy/consent/${tenantId}/${consentId}`),
  getNERStatus: () =>
    rawApi.get<NERStatus>('/api/v1/pii-proxy/ner/status').then((r) => r.data).catch((err) => {
      if (err.response?.status === 503) {
        return { available: false, model_version: 'n/a', load_time_ms: 0, gpu_enabled: false, error: 'NER service not enabled' } as NERStatus
      }
      throw err
    }),
}

// ---------- Sub-Components ----------

function StatusCard({ label, value, icon: Icon, color }: {
  label: string
  value: string | number
  icon: typeof ShieldCheckIcon
  color: string
}) {
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold text-foreground">{value}</p>
        </div>
      </div>
    </Card>
  )
}

// ---------- Dashboard Tab ----------

function DashboardTab() {
  const { data: status, isLoading: loadingStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['pii-proxy-status'],
    queryFn: piiApi.getStatus,
    refetchInterval: 30000,
  })

  const { data: nerStatus, isLoading: loadingNER } = useQuery({
    queryKey: ['pii-proxy-ner-status'],
    queryFn: piiApi.getNERStatus,
    refetchInterval: 60000,
  })

  const { data: projects } = useQuery({
    queryKey: ['pii-proxy-projects'],
    queryFn: piiApi.getProjects,
    refetchInterval: 30000,
  })

  // Build detection category chart data from projects (placeholder simulation)
  const categoryData = projects
    ? [
        { name: 'Active', count: status?.active_projects ?? 0, fill: '#22c55e' },
        { name: 'Shadow', count: status?.shadow_projects ?? 0, fill: '#f59e0b' },
        { name: 'Disabled', count: (status?.registered_projects ?? 0) - (status?.active_projects ?? 0) - (status?.shadow_projects ?? 0), fill: '#94a3b8' },
      ].filter((d) => d.count > 0)
    : []

  if (loadingStatus || loadingNER) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-20 bg-muted rounded" />
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Refresh */}
      <div className="flex justify-end">
        <Button variant="secondary" size="sm" onClick={() => refetchStatus()}>
          <ArrowPathIcon className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Summary Banner */}
      {status && (
        <Card className="bg-gradient-to-r from-indigo-500 to-violet-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold opacity-90">PII Proxy Service</h2>
              <p className="text-3xl font-bold mt-2">
                {status.enabled ? 'Active' : 'Disabled'}
              </p>
              <p className="text-sm opacity-80 mt-1">
                Pattern version {status.pattern_version}
              </p>
            </div>
            <div className="text-right">
              <ShieldCheckIcon className="w-10 h-10 opacity-80 mb-2" />
              {status.uptime_seconds !== undefined && (
                <p className="text-sm opacity-80">
                  Uptime: {Math.floor(status.uptime_seconds / 3600)}h {Math.floor((status.uptime_seconds % 3600) / 60)}m
                </p>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatusCard
          label="Service Status"
          value={status?.enabled ? 'Enabled' : 'Disabled'}
          icon={status?.enabled ? CheckCircleIcon : XCircleIcon}
          color={status?.enabled ? 'bg-green-500' : 'bg-red-500'}
        />
        <StatusCard
          label="Pattern Version"
          value={status?.pattern_version || 'N/A'}
          icon={ShieldCheckIcon}
          color="bg-indigo-500"
        />
        <StatusCard
          label="Registered Projects"
          value={status?.registered_projects ?? 0}
          icon={ClipboardDocumentListIcon}
          color="bg-blue-500"
        />
        <StatusCard
          label="NER Engine"
          value={nerStatus?.available ? nerStatus.model_version : 'Tier 1+2 only'}
          icon={CpuChipIcon}
          color={nerStatus?.available ? 'bg-purple-500' : 'bg-muted-foreground'}
        />
      </div>

      {/* Mode Breakdown + Detection Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Mode Distribution */}
        <Card>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <ClipboardDocumentListIcon className="w-5 h-5 text-primary" />
            Project Mode Distribution
          </h3>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="count"
                  label={({ name, count }) => `${name}: ${count}`}
                  labelLine={false}
                >
                  {categoryData.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  formatter={(value) => <span className="text-xs">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-muted-foreground">
              No projects registered yet
            </div>
          )}
        </Card>

        {/* Detection Metrics Placeholder */}
        <Card>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <MagnifyingGlassIcon className="w-5 h-5 text-primary" />
            Detection Metrics
          </h3>
          <div className="flex items-center justify-center h-[220px]">
            <div className="text-center text-muted-foreground">
              <BarChart width={300} height={180}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
              <p className="text-sm mt-2">Detection metrics will populate as scans are performed</p>
            </div>
          </div>
        </Card>
      </div>

      {/* NER Status Detail */}
      {nerStatus && (
        <Card>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <CpuChipIcon className="w-5 h-5 text-purple-600" />
            NER Engine (Tier 3 detection)
          </h3>

          {nerStatus.available ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <Badge variant="success">loaded</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Model</span>
                <span className="text-sm font-mono text-foreground">{nerStatus.model_version}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Load time</span>
                <span className="text-sm text-foreground">{Math.round(nerStatus.load_time_ms / 1000)}s</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">GPU</span>
                <Badge variant={nerStatus.gpu_enabled ? 'success' : 'gray'}>{nerStatus.gpu_enabled ? 'Yes' : 'CPU'}</Badge>
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Badge variant="gray">Not loaded</Badge>
                <span className="text-muted-foreground">
                  Tier 3 semantic detection (PERSON / ORG / LOCATION names) is off.
                </span>
              </div>
              <div className="p-3 rounded-lg bg-muted/40 border border-border text-muted-foreground space-y-2">
                <p>
                  <strong className="text-foreground">Tier 1 + 2 are still active</strong> and catch all
                  structured PII: SSN, email, phone, credit cards (Luhn-validated), IBAN, NHS number,
                  EIN, VAT, and every other regex/validator category.
                </p>
                <p>
                  To enable name/org/location detection, set{' '}
                  <code className="text-xs font-mono bg-background px-1 rounded border border-border">
                    PII_PROXY_ENABLE_NER=true
                  </code>{' '}
                  in the agent-backend env and rebuild. Adds ~1&nbsp;GB RAM and ~10s to startup
                  (spaCy en_core_web_trf model).
                </p>
                {nerStatus.error && (
                  <p className="text-[11px] text-muted-foreground/80">
                    <span className="font-mono">backend:</span> {nerStatus.error}
                  </p>
                )}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

// ---------- Projects Tab ----------

function ProjectsTab() {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingProject, setEditingProject] = useState<PIIProject | null>(null)
  const [formData, setFormData] = useState({ name: '', tenant_id: '', description: '', enabled: true, shadow_mode: false })

  const { data: projects, isLoading } = useQuery({
    queryKey: ['pii-proxy-projects'],
    queryFn: piiApi.getProjects,
    refetchInterval: 15000,
  })

  const registerMutation = useMutation({
    mutationFn: (data: typeof formData) => piiApi.registerProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pii-proxy-projects'] })
      queryClient.invalidateQueries({ queryKey: ['pii-proxy-status'] })
      setShowForm(false)
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PIIProject> }) => piiApi.updateProject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pii-proxy-projects'] })
      queryClient.invalidateQueries({ queryKey: ['pii-proxy-status'] })
      setEditingProject(null)
      resetForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => piiApi.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pii-proxy-projects'] })
      queryClient.invalidateQueries({ queryKey: ['pii-proxy-status'] })
    },
  })

  const resetForm = () => setFormData({ name: '', tenant_id: '', description: '', enabled: true, shadow_mode: false })

  const handleToggle = (project: PIIProject, field: 'enabled' | 'shadow_mode') => {
    updateMutation.mutate({ id: project.id, data: { [field]: !project[field] } })
  }

  const handleEdit = (project: PIIProject) => {
    setEditingProject(project)
    setFormData({
      name: project.name,
      tenant_id: project.tenant_id,
      description: project.description || '',
      enabled: project.enabled,
      shadow_mode: project.shadow_mode,
    })
    setShowForm(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingProject) {
      updateMutation.mutate({ id: editingProject.id, data: formData })
    } else {
      registerMutation.mutate(formData)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Registered Projects</h3>
        <Button
          size="sm"
          onClick={() => {
            setEditingProject(null)
            resetForm()
            setShowForm(!showForm)
          }}
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Register Project
        </Button>
      </div>

      {/* Registration / Edit Form */}
      {showForm && (
        <Card className="border-indigo-500/20 bg-indigo-500/10/30">
          <h4 className="font-semibold text-foreground mb-4">
            {editingProject ? 'Edit Project' : 'Register New Project'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Project Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-indigo-500"
                  placeholder="my-service"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Tenant ID</label>
                <input
                  type="text"
                  value={formData.tenant_id}
                  onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-indigo-500"
                  placeholder="tenant-001"
                  required
                  disabled={!!editingProject}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-indigo-500"
                placeholder="Optional description"
              />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="rounded border-border text-primary focus:ring-ring"
                />
                Enabled
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.shadow_mode}
                  onChange={(e) => setFormData({ ...formData, shadow_mode: e.target.checked })}
                  className="rounded border-border text-primary focus:ring-ring"
                />
                Shadow Mode
              </label>
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" size="sm" isLoading={registerMutation.isPending || updateMutation.isPending}>
                {editingProject ? 'Update Project' : 'Register'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowForm(false)
                  setEditingProject(null)
                  resetForm()
                }}
              >
                Cancel
              </Button>
            </div>
            {(registerMutation.isError || updateMutation.isError) && (
              <div className="p-2 bg-red-500/10 rounded text-xs text-red-600">
                {(registerMutation.error as any)?.response?.data?.detail ||
                  (updateMutation.error as any)?.response?.data?.detail ||
                  'Operation failed'}
              </div>
            )}
          </form>
        </Card>
      )}

      {/* Projects Table */}
      {isLoading ? (
        <Card className="animate-pulse">
          <div className="h-48 bg-muted rounded" />
        </Card>
      ) : projects && projects.length > 0 ? (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Tenant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Mode</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-gray-200">
                {projects.map((project) => (
                  <tr key={project.id} className="hover:bg-muted/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-foreground">{project.name}</div>
                      {project.description && (
                        <div className="text-xs text-muted-foreground">{project.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground font-mono">
                      {project.tenant_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggle(project, 'enabled')}
                        className="focus:outline-none"
                        title={project.enabled ? 'Click to disable' : 'Click to enable'}
                      >
                        <Badge variant={project.enabled ? 'success' : 'error'}>
                          {project.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggle(project, 'shadow_mode')}
                        className="focus:outline-none flex items-center gap-1"
                        title={project.shadow_mode ? 'Click to exit shadow mode' : 'Click to enter shadow mode'}
                      >
                        {project.shadow_mode ? (
                          <EyeSlashIcon className="w-4 h-4 text-yellow-500" />
                        ) : (
                          <EyeIcon className="w-4 h-4 text-green-500" />
                        )}
                        <Badge variant={project.shadow_mode ? 'warning' : 'gray'} size="sm">
                          {project.shadow_mode ? 'Shadow' : 'Active'}
                        </Badge>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {new Date(project.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(project)}
                          className="p-1.5 text-muted-foreground hover:text-primary rounded-md hover:bg-indigo-500/10 transition-colors"
                          title="Edit project"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete project "${project.name}"?`)) {
                              deleteMutation.mutate(project.id)
                            }
                          }}
                          className="p-1.5 text-muted-foreground hover:text-red-600 rounded-md hover:bg-red-500/10 transition-colors"
                          title="Delete project"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="text-center py-12 text-muted-foreground">
            <ClipboardDocumentListIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
            <p className="font-medium">No projects registered</p>
            <p className="text-sm mt-1">Register a project to start scanning for PII.</p>
          </div>
        </Card>
      )}
    </div>
  )
}

// ---------- Scanner Test Tab ----------

function ScannerTestTab() {
  const [scanText, setScanText] = useState('')

  const scanMutation = useMutation({
    mutationFn: (text: string) => piiApi.testScan(text),
  })

  const handleScan = (e: React.FormEvent) => {
    e.preventDefault()
    if (scanText.trim()) {
      scanMutation.mutate(scanText)
    }
  }

  const result = scanMutation.data

  return (
    <div className="space-y-6">
      {/* Input */}
      <Card>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <MagnifyingGlassIcon className="w-5 h-5 text-primary" />
          PII Scanner Test
        </h3>
        <form onSubmit={handleScan} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Enter text to scan for PII
            </label>
            <textarea
              value={scanText}
              onChange={(e) => setScanText(e.target.value)}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-indigo-500 font-mono"
              rows={6}
              placeholder="Paste text here to test PII detection. Example: My name is John Doe, my email is john@example.com and my SSN is 123-45-6789."
            />
          </div>
          <div className="flex items-center gap-3">
            <Button type="submit" isLoading={scanMutation.isPending} disabled={!scanText.trim()}>
              <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
              Scan
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setScanText('')
                scanMutation.reset()
              }}
            >
              Clear
            </Button>
          </div>
        </form>
      </Card>

      {/* Error */}
      {scanMutation.isError && (
        <Card className="bg-red-500/10 border-red-500/20">
          <div className="flex items-center gap-2 text-red-600">
            <ExclamationTriangleIcon className="w-5 h-5" />
            <span>{(scanMutation.error as any)?.response?.data?.detail || 'Scan failed'}</span>
          </div>
        </Card>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Summary */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Scan Results</h3>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span>{result.matches.length} match{result.matches.length !== 1 ? 'es' : ''}</span>
                <span>{result.scan_duration_ms.toFixed(2)} ms</span>
                <span>{scanText.length} chars</span>
                {result.pattern_version && (
                  <Badge variant="gray" size="sm" title="Pattern rule set version">
                    v{result.pattern_version}
                  </Badge>
                )}
              </div>
            </div>

            {result.categories_found.length > 0 && (
              <div className="mb-4">
                <span className="text-sm text-muted-foreground mr-2">Categories:</span>
                {result.categories_found.map((c) => (
                  <Badge key={c} variant="gray" size="sm" className="mr-1">
                    {c}
                  </Badge>
                ))}
              </div>
            )}

            {/* Highlighted text preview */}
            {result.matches.length > 0 && (
              <div className="p-3 bg-muted/50 rounded-lg font-mono text-sm leading-relaxed break-all">
                <HighlightedText text={scanText} matches={result.matches} />
              </div>
            )}
          </Card>

          {/* Match Details */}
          {result.matches.length > 0 ? (
            <Card padding="none">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Value (masked)</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Confidence</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Offsets</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Tier</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Pattern</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-gray-200">
                    {result.matches.map((match, idx) => (
                      <tr key={idx} className="hover:bg-muted/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge
                            variant={match.confidence >= 0.9 ? 'error' : match.confidence >= 0.7 ? 'warning' : 'gray'}
                            size="sm"
                          >
                            {match.category}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-foreground">
                          {match.value}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-muted rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  match.confidence >= 0.9
                                    ? 'bg-red-500'
                                    : match.confidence >= 0.7
                                      ? 'bg-yellow-500'
                                      : 'bg-muted-foreground'
                                }`}
                                style={{ width: `${Math.round(match.confidence * 100)}%` }}
                              />
                            </div>
                            <span className="text-sm text-muted-foreground">
                              {(match.confidence * 100).toFixed(0)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground font-mono">
                          [{match.start}:{match.end}]
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <Badge
                            variant={match.tier === 3 ? 'primary' : match.tier === 2 ? 'success' : 'gray'}
                            size="sm"
                            title={match.tier === 1 ? 'Regex' : match.tier === 2 ? 'Validator (Luhn/checksum)' : 'NER (semantic)'}
                          >
                            T{match.tier}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground font-mono">
                          {match.pattern_name}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircleIcon className="w-10 h-10 mx-auto mb-2 text-green-400" />
                <p className="font-medium">No PII detected</p>
                <p className="text-sm mt-1">The scanned text appears clean.</p>
              </div>
            </Card>
          )}

          {/* Category breakdown chart */}
          {result.matches.length > 0 && (
            <Card>
              <h4 className="text-sm font-semibold text-foreground mb-3">Detection by Category</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={Object.entries(
                    result.matches.reduce<Record<string, number>>((acc, m) => {
                      acc[m.category] = (acc[m.category] || 0) + 1
                      return acc
                    }, {})
                  ).map(([cat, count]) => ({ name: cat, count, fill: CATEGORY_COLORS[cat] || '#6366f1' }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {Object.entries(
                      result.matches.reduce<Record<string, number>>((acc, m) => {
                        acc[m.category] = (acc[m.category] || 0) + 1
                        return acc
                      }, {})
                    ).map(([cat], idx) => (
                      <Cell key={idx} fill={CATEGORY_COLORS[cat] || CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}

function HighlightedText({ text, matches }: { text: string; matches: PIIMatch[] }) {
  if (matches.length === 0) return <span>{text}</span>

  // Sort by start offset
  const sorted = [...matches].sort((a, b) => a.start - b.start)
  const segments: JSX.Element[] = []
  let cursor = 0

  sorted.forEach((match, idx) => {
    if (match.start > cursor) {
      segments.push(<span key={`t-${idx}`}>{text.slice(cursor, match.start)}</span>)
    }
    const color = CATEGORY_COLORS[match.category] || '#6366f1'
    segments.push(
      <span
        key={`m-${idx}`}
        className="px-0.5 rounded font-semibold"
        style={{ backgroundColor: `${color}25`, color, textDecoration: 'underline', textDecorationStyle: 'wavy' }}
        title={`${match.category} (${(match.confidence * 100).toFixed(0)}%)`}
      >
        {text.slice(match.start, match.end)}
      </span>
    )
    cursor = match.end
  })

  if (cursor < text.length) {
    segments.push(<span key="tail">{text.slice(cursor)}</span>)
  }

  return <>{segments}</>
}

// ---------- Custom Registry Tab ----------

function RegistryTab() {
  const queryClient = useQueryClient()
  const [tenantId, setTenantId] = useState('')
  const [activeTenant, setActiveTenant] = useState('')
  const [newTerm, setNewTerm] = useState('')
  const [newCategory, setNewCategory] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['pii-registry-stats', activeTenant],
    queryFn: () => piiApi.getRegistryStats(activeTenant),
    enabled: !!activeTenant,
  })

  const { data: terms, isLoading: loadingTerms } = useQuery({
    queryKey: ['pii-registry-terms', activeTenant],
    queryFn: () => piiApi.getRegistryTerms(activeTenant),
    enabled: !!activeTenant,
  })

  const addMutation = useMutation({
    mutationFn: () => piiApi.addRegistryTerm(activeTenant, newTerm, newCategory),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pii-registry-terms', activeTenant] })
      queryClient.invalidateQueries({ queryKey: ['pii-registry-stats', activeTenant] })
      setNewTerm('')
      setNewCategory('')
    },
  })

  const removeMutation = useMutation({
    mutationFn: (termId: string) => piiApi.removeRegistryTerm(activeTenant, termId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pii-registry-terms', activeTenant] })
      queryClient.invalidateQueries({ queryKey: ['pii-registry-stats', activeTenant] })
    },
  })

  const importMutation = useMutation({
    mutationFn: (file: File) => piiApi.importCSV(activeTenant, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pii-registry-terms', activeTenant] })
      queryClient.invalidateQueries({ queryKey: ['pii-registry-stats', activeTenant] })
    },
  })

  const handleLoadTenant = (e: React.FormEvent) => {
    e.preventDefault()
    if (tenantId.trim()) {
      setActiveTenant(tenantId.trim())
    }
  }

  const handleAddTerm = (e: React.FormEvent) => {
    e.preventDefault()
    if (newTerm.trim() && newCategory.trim()) {
      addMutation.mutate()
    }
  }

  const handleCSVImport = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      importMutation.mutate(file)
      e.target.value = ''
    }
  }

  // Build chart data from stats categories
  const categoryChartData = stats
    ? Object.entries(stats.categories).map(([cat, count], idx) => ({
        name: cat,
        count,
        fill: CHART_COLORS[idx % CHART_COLORS.length],
      }))
    : []

  return (
    <div className="space-y-6">
      {/* Tenant Selector */}
      <Card>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <FingerPrintIcon className="w-5 h-5 text-primary" />
          Custom PII Registry
        </h3>
        <form onSubmit={handleLoadTenant} className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-foreground mb-1">Tenant ID</label>
            <input
              type="text"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-indigo-500"
              placeholder="Enter tenant ID to load registry"
            />
          </div>
          <Button type="submit" size="sm" disabled={!tenantId.trim()}>
            Load Registry
          </Button>
        </form>
      </Card>

      {!activeTenant && (
        <Card>
          <div className="text-center py-12 text-muted-foreground">
            <FingerPrintIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
            <p className="font-medium">Enter a tenant ID above to view and manage custom PII terms</p>
          </div>
        </Card>
      )}

      {activeTenant && (
        <>
          {/* Stats */}
          {loadingStats ? (
            <Card className="animate-pulse">
              <div className="h-24 bg-muted rounded" />
            </Card>
          ) : stats ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-foreground">Registry Stats</h4>
                  <Badge variant="primary">{activeTenant}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-indigo-500/10 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-primary">{stats.total_terms}</div>
                    <div className="text-sm text-primary">Total Terms</div>
                  </div>
                  <div className="bg-indigo-500/10 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-primary">{Object.keys(stats.categories).length}</div>
                    <div className="text-sm text-primary">Categories</div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                  Last updated: {new Date(stats.last_updated).toLocaleString()}
                </div>
              </Card>

              {categoryChartData.length > 0 && (
                <Card>
                  <h4 className="font-semibold text-foreground mb-3">Terms by Category</h4>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={categoryChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {categoryChartData.map((entry, idx) => (
                          <Cell key={idx} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}
            </div>
          ) : null}

          {/* Add Term + CSV Import */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-foreground">Add Term</h4>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelected}
                  accept=".csv"
                  className="hidden"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCSVImport}
                  isLoading={importMutation.isPending}
                >
                  <DocumentArrowUpIcon className="w-4 h-4 mr-1" />
                  Import CSV
                </Button>
              </div>
            </div>
            <form onSubmit={handleAddTerm} className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground mb-1">Term</label>
                <input
                  type="text"
                  value={newTerm}
                  onChange={(e) => setNewTerm(e.target.value)}
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-indigo-500"
                  placeholder="e.g. ACME Corp"
                />
              </div>
              <div className="w-48">
                <label className="block text-sm font-medium text-foreground mb-1">Category</label>
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-indigo-500"
                  placeholder="e.g. ORG_NAME"
                />
              </div>
              <Button type="submit" size="sm" isLoading={addMutation.isPending} disabled={!newTerm.trim() || !newCategory.trim()}>
                <PlusIcon className="w-4 h-4 mr-1" />
                Add
              </Button>
            </form>
            {(addMutation.isError || importMutation.isError) && (
              <div className="mt-2 p-2 bg-red-500/10 rounded text-xs text-red-600">
                {(addMutation.error as any)?.response?.data?.detail ||
                  (importMutation.error as any)?.response?.data?.detail ||
                  'Operation failed'}
              </div>
            )}
          </Card>

          {/* Terms List */}
          {loadingTerms ? (
            <Card className="animate-pulse">
              <div className="h-32 bg-muted rounded" />
            </Card>
          ) : terms && terms.length > 0 ? (
            <Card padding="none">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Term</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Added</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-gray-200">
                    {terms.map((term) => (
                      <tr key={term.id} className="hover:bg-muted/50">
                        <td className="px-6 py-3 whitespace-nowrap font-medium text-foreground text-sm">
                          {term.term}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <Badge variant="primary" size="sm">{term.category}</Badge>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-muted-foreground">
                          {new Date(term.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right">
                          <button
                            onClick={() => removeMutation.mutate(term.id)}
                            className="p-1.5 text-muted-foreground hover:text-red-600 rounded-md hover:bg-red-500/10 transition-colors"
                            title="Remove term"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">No custom terms in this registry yet.</p>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

// ---------- Consent Tab ----------

function ConsentTab() {
  const queryClient = useQueryClient()
  const [tenantId, setTenantId] = useState('')
  const [activeTenant, setActiveTenant] = useState('')
  const [grantUserId, setGrantUserId] = useState('')
  const [grantScope, setGrantScope] = useState('')

  const { data: records, isLoading } = useQuery({
    queryKey: ['pii-consent', activeTenant],
    queryFn: () => piiApi.getConsentRecords(activeTenant),
    enabled: !!activeTenant,
  })

  const grantMutation = useMutation({
    mutationFn: () => piiApi.grantConsent(activeTenant, grantUserId, grantScope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pii-consent', activeTenant] })
      setGrantUserId('')
      setGrantScope('')
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (consentId: string) => piiApi.revokeConsent(activeTenant, consentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pii-consent', activeTenant] })
    },
  })

  const handleLoadTenant = (e: React.FormEvent) => {
    e.preventDefault()
    if (tenantId.trim()) {
      setActiveTenant(tenantId.trim())
    }
  }

  const handleGrant = (e: React.FormEvent) => {
    e.preventDefault()
    if (grantUserId.trim() && grantScope.trim()) {
      grantMutation.mutate()
    }
  }

  return (
    <div className="space-y-6">
      {/* Tenant Selector */}
      <Card>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <UserGroupIcon className="w-5 h-5 text-primary" />
          Consent Management
        </h3>
        <form onSubmit={handleLoadTenant} className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-foreground mb-1">Tenant ID</label>
            <input
              type="text"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-indigo-500"
              placeholder="Enter tenant ID"
            />
          </div>
          <Button type="submit" size="sm" disabled={!tenantId.trim()}>
            Load Consent Records
          </Button>
        </form>
      </Card>

      {!activeTenant && (
        <Card>
          <div className="text-center py-12 text-muted-foreground">
            <UserGroupIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
            <p className="font-medium">Enter a tenant ID above to manage consent records</p>
          </div>
        </Card>
      )}

      {activeTenant && (
        <>
          {/* Grant Consent Form */}
          <Card>
            <h4 className="font-semibold text-foreground mb-4">Grant Consent</h4>
            <form onSubmit={handleGrant} className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground mb-1">User ID</label>
                <input
                  type="text"
                  value={grantUserId}
                  onChange={(e) => setGrantUserId(e.target.value)}
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-indigo-500"
                  placeholder="user-123"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground mb-1">Scope</label>
                <input
                  type="text"
                  value={grantScope}
                  onChange={(e) => setGrantScope(e.target.value)}
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-indigo-500"
                  placeholder="e.g. pii:read, pii:full, analytics"
                />
              </div>
              <Button type="submit" size="sm" isLoading={grantMutation.isPending} disabled={!grantUserId.trim() || !grantScope.trim()}>
                <PlusIcon className="w-4 h-4 mr-1" />
                Grant
              </Button>
            </form>
            {grantMutation.isError && (
              <div className="mt-2 p-2 bg-red-500/10 rounded text-xs text-red-600">
                {(grantMutation.error as any)?.response?.data?.detail || 'Failed to grant consent'}
              </div>
            )}
          </Card>

          {/* Consent Records */}
          {isLoading ? (
            <Card className="animate-pulse">
              <div className="h-32 bg-muted rounded" />
            </Card>
          ) : records && records.length > 0 ? (
            <Card padding="none">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Scope</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Granted</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Updated</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-gray-200">
                    {records.map((record) => (
                      <tr key={record.id} className="hover:bg-muted/50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-foreground">
                          {record.user_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant="primary" size="sm">{record.scope}</Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={record.granted ? 'success' : 'error'} size="sm">
                            {record.granted ? 'Granted' : 'Revoked'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {new Date(record.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {new Date(record.updated_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {record.granted && (
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => {
                                if (window.confirm(`Revoke consent for user "${record.user_id}" (scope: ${record.scope})?`)) {
                                  revokeMutation.mutate(record.id)
                                }
                              }}
                              isLoading={revokeMutation.isPending}
                            >
                              Revoke
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="text-center py-8 text-muted-foreground">
                <UserGroupIcon className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                <p className="font-medium">No consent records</p>
                <p className="text-sm mt-1">Use the form above to grant consent to users.</p>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

// ---------- Main Page ----------

export default function PIIProxyPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('dashboard')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
            <ShieldCheckIcon className="w-7 h-7 text-primary" />
            PII Proxy
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Manage PII detection, masking, project registration, and consent
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-6 overflow-x-auto" aria-label="Tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`
                flex items-center gap-2 whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors
                ${
                  activeTab === key
                    ? 'border-indigo-500 text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }
              `}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && <DashboardTab />}
      {activeTab === 'projects' && <ProjectsTab />}
      {activeTab === 'scanner' && <ScannerTestTab />}
      {activeTab === 'registry' && <RegistryTab />}
      {activeTab === 'consent' && <ConsentTab />}
    </div>
  )
}
